/** @jsxImportSource @emotion/react */

import Quote from '@design-system/quote';
import Canvas from '@design-system/canvas';
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { v4 as uuid4 } from 'uuid';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import Chip from '@design-system/chip';
import { ETheme } from '@datatype/type';
import { IAiVocabulary } from '@datatype/iyagigo/api-type';
import { VOCABULARY_WORD_CLASS_LABEL } from '@datatype/iyagigo/type';
import Gap from '@component/common/gap';
import Placeholder from '@component/common/placeholder';
import Scroll, { ScrollContainer } from '@component/common/scroll';

interface IProps {
  aiVocabulary: IAiVocabulary;
  levelLabel?: string;
  isAnswerView: boolean;
  handleClick: () => void;
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  // padding: 10px; // box shadow때문에
`;

const BadgeRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: 5px;
`;

const MeaningAndPronuncationContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: 5px;
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  //border-top: 1px solid ${color.GRAY_3};
`;

const KanjisContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 5px;
  width: 100%;
  padding: 0 0 0 ${size.MARGIN_1};
`;

const KanjiContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: baseline;
  gap: 5px;
`;

const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
`;

const HintText = styled.div`
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  gap: 4px;

  & svg {
    animation: ${bounce} 1s infinite ease-in-out;
  }
`;

const renderExampleWithEmphasis = (example: string): React.ReactNode => {
  // [단어] 패턴 강조
  const parts = example.split(/(\[[^\]]+\])/g);
  return parts.map((part, idx) => {
    if (part.startsWith('[') && part.endsWith(']')) {
      const inner = part.slice(1, -1);
      return (
        <span key={idx} style={{ fontWeight: 700, color: color.BROWN_1 }}>
          {inner}
        </span>
      );
    }
    return <span key={idx}>{part}</span>;
  });
};

const VocabularyCard: React.FC<IProps> = ({ aiVocabulary, levelLabel, isAnswerView, handleClick }) => {
  // ruby는 첫 번째 발음만 (parseFurigana가 multi-reading 매칭을 지원하지 않음)
  const primaryPronunciation = aiVocabulary.sourcePronunciation[0] ?? '';
  // 발음이 2개 이상일 때만 보조 라인 표시
  const extraPronunciation =
    aiVocabulary.sourcePronunciation.length > 1 ? aiVocabulary.sourcePronunciation.join(' / ') : '';
  const meaning = aiVocabulary.meaningKo.join(', ');
  const wordClass = aiVocabulary.wordClass
    ? (VOCABULARY_WORD_CLASS_LABEL[aiVocabulary.wordClass] ?? aiVocabulary.wordClass)
    : '';
  const example = aiVocabulary.exampleKo?.[0];
  const kanjis = aiVocabulary.kanjiKo ?? [];

  const Kanji = kanjis.map((item) => (
    <KanjiContainer key={uuid4()}>
      <TextBlock
        text={item.character}
        design={{ text: { size: 'large', color: color.BLACK_2, bold: false }, box: { alignX: 'left' } }}
      />
      {item.meaning?.[0] && (
        <TextBlock
          text={item.meaning[0]}
          design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
        />
      )}
    </KanjiContainer>
  ));

  return (
    <Wrapper>
      <Canvas
        theme={ETheme.PAPER}
        shape='round'
        width='100%'
        height='100%'
        padding={size.PAGE_PADDING}
        alignX='center'
        shadow
        handleClick={handleClick}
      >
        <BadgeRow>
          {levelLabel && <Chip text={levelLabel} size='small' usage='static' kind='filled' theme={ETheme.BROWN} />}
          {wordClass && <Chip text={wordClass} size='small' usage='static' kind='outlined' theme={ETheme.BROWN} />}
        </BadgeRow>
        <Gap height={size.MARGIN_4} />
        <TextBlock
          text={aiVocabulary.source}
          annotation={
            primaryPronunciation
              ? { reading: primaryPronunciation, color: isAnswerView ? undefined : 'transparent' }
              : undefined
          }
          reserveAnnotationSpace={!!primaryPronunciation}
          design={{
            text: { size: 'giant', color: color.BLACK_2, bold: true },
            box: {},
          }}
        />
        <Gap height={size.MARGIN_1} />
        <ScrollContainer width={'100%'}>
          <Scroll direction='y' height='100%'>
            <Placeholder visible={isAnswerView} placeholder={null}>
              <MeaningAndPronuncationContainer>
                {extraPronunciation && (
                  <TextBlock
                    text={extraPronunciation}
                    design={{ text: { size: 'small', color: color.GRAY_1 }, box: {} }}
                  />
                )}
                <TextBlock
                  text={meaning}
                  design={{ text: { size: 'large', color: color.BROWN_1, bold: true }, box: {} }}
                />
              </MeaningAndPronuncationContainer>
            </Placeholder>
            {isAnswerView && (example?.sentence || kanjis.length > 0) && (
              <DescriptionContainer>
                <Gap height={size.MARGIN_1} />
                {kanjis.length > 0 && <KanjisContainer>{Kanji}</KanjisContainer>}
                <Gap height={size.MARGIN_1} />
                {example?.sentence && example?.translation && (
                  <Quote width='100%'>
                    <TextBlock
                      text={example.sentence}
                      design={{ text: { size: 'medium', color: color.BLACK_2 }, box: { alignX: 'left' } }}
                    />
                    <TextBlock
                      text={example.translation}
                      design={{ text: { size: 'small', color: color.GRAY_1 }, box: { alignX: 'left' } }}
                    />
                  </Quote>
                )}
              </DescriptionContainer>
            )}
          </Scroll>
        </ScrollContainer>
        {/* {!isAnswerView && (
          <HintText>
            <IconBlock
              icon='touch-app'
              design={{ icon: { size: 'medium', color: color.BROWN_2, kind: 'outlined' }, box: {} }}
            />
            <TextBlock
              text='탭하여 뜻 보기'
              design={{ text: { size: 'medium', color: color.BROWN_2, bold: true }, box: {} }}
            />
          </HintText>
        )} */}
      </Canvas>
    </Wrapper>
  );
};

export default VocabularyCard;
